import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const LOGO_SRC = '/twealth.png';

// Generate a short reference number for the receipt
function getReceiptReference(transaction) {
  const id = (transaction.id || 'mock').toString().replace(/\D/g, '');
  const date = transaction.date instanceof Date
    ? transaction.date.getTime()
    : new Date(transaction.date).getTime();
  const str = `${id}-${date}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + c;
    hash = hash & hash;
  }
  const ref = String(Math.abs(hash)).padStart(10, '0').slice(-10);
  return `NS-${ref}`;
}

// Simple 1D barcode-style stripes (decorative) from reference
function BarcodeStripes({ reference }) {
  const digits = reference.replace(/\D/g, '').split('').map(Number);
  const widths = digits.length ? digits : [1, 2, 3, 4, 5, 6, 7, 8, 9];
  return (
    <div className="flex items-end justify-center gap-0.5 h-8 sm:h-10">
      {widths.map((w, i) => (
        <div
          key={i}
          className="bg-gray-900 rounded-sm"
          style={{ width: Math.max(2, w * 2), height: `${18 + (i % 3) * 6}px` }}
        />
      ))}
    </div>
  );
}

export default function TransactionReceiptModal({ transaction, userData, formatCurrency, formatDate, onClose }) {
  const receiptRef = useRef(null);

  if (!transaction) return null;

  const date = transaction.date instanceof Date ? transaction.date : new Date(transaction.date);
  const reference = getReceiptReference(transaction);
  const qrPayload = JSON.stringify({
    ref: reference,
    amount: transaction.amount,
    type: transaction.type,
    date: date.toISOString(),
    desc: (transaction.description || '').slice(0, 50)
  });

  const handlePrint = () => {
    const content = receiptRef.current;
    if (!content) return;
    const base = window.location.origin;
    const win = window.open('', '_blank');
    const receiptHtml = content.innerHTML
      .replace(/src="\/images\//g, `src="${base}/images/`)
      .replace(/src="\/twealth\.png"/g, `src="${base}/twealth.png"`);
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt - ${reference}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Georgia', serif; background: #f8f9fa; padding: 24px; }
            .receipt { max-width: 400px; margin: 0 auto; background: #fff; padding: 32px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); }
            .receipt img { height: 48px; width: auto; display: block; margin-bottom: 20px; }
            .receipt h1 { font-size: 11px; letter-spacing: 0.2em; color: #374151; margin-bottom: 24px; }
            .receipt .row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
            .receipt .row .label { color: #6b7280; }
            .receipt .amount { font-size: 18px; font-weight: 700; margin: 16px 0; }
            .receipt .qr { margin: 20px auto; text-align: center; }
            .receipt .qr svg { display: block; margin: 0 auto; }
            .receipt .barcode { margin: 12px 0; }
            .receipt .ref { font-size: 12px; letter-spacing: 0.15em; color: #374151; margin-top: 8px; }
            .receipt .footer { margin-top: 24px; font-size: 11px; color: #9ca3af; text-align: center; }
          </style>
        </head>
        <body><div class="receipt">${receiptHtml}</div></body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.close();
    }, 250);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 dark:bg-black/70 animate-fadeIn" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] sm:max-h-[88vh] flex flex-col animate-fadeIn overflow-hidden border border-slate-200 dark:border-slate-600"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header with close */}
        <div className="flex items-center justify-end p-2 sm:p-3 border-b border-gray-100 dark:border-slate-700 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-slate-200 transition-colors touch-manipulation"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Receipt content - scrollable, compact */}
        <div ref={receiptRef} className="p-4 sm:p-5 pb-4 bg-[#fafaf9] dark:bg-slate-800 overflow-y-auto flex-1 min-h-0">
          <img src={LOGO_SRC} alt="Willowvale Credit Union" className="h-9 sm:h-11 w-auto mb-3 sm:mb-4" />
          <h1 className="text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-slate-400 tracking-[0.2em] sm:tracking-[0.25em] uppercase mb-3 sm:mb-4">
            Payment Receipt
          </h1>

          <div className="space-y-0.5 sm:space-y-1 mb-3 sm:mb-4">
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 dark:text-slate-400 shrink-0">Reference</span>
              <span className="font-mono font-medium text-gray-900 dark:text-slate-100 truncate text-right">{reference}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 dark:text-slate-400 shrink-0">Date & time</span>
              <span className="text-gray-900 dark:text-slate-100 text-right">{formatDate(date)}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 dark:text-slate-400 shrink-0">Description</span>
              <span className="text-gray-900 dark:text-slate-100 text-right max-w-[55%] sm:max-w-[200px] truncate" title={transaction.description}>{transaction.description}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 dark:text-slate-400 shrink-0">Status</span>
              <span className="text-gray-900 dark:text-slate-100 capitalize">{transaction.status || 'Completed'}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-500 dark:text-slate-400 shrink-0">Type</span>
              <span className="text-gray-900 dark:text-slate-100 capitalize">{transaction.type}</span>
            </div>
          </div>

          <div className={`py-2.5 sm:py-3 border-y border-gray-200 dark:border-slate-600 my-3 sm:my-4 text-base sm:text-lg font-bold ${
            transaction.type === 'credit' ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-white'
          }`}>
            {transaction.type === 'credit' ? '+' : '−'} {formatCurrency(Math.abs(transaction.amount))}
          </div>

          <div className="qr flex flex-col items-center mt-3 sm:mt-4">
            <QRCodeSVG value={qrPayload} size={96} level="M" className="mb-2 sm:mb-3 w-20 h-20 sm:w-24 sm:h-24" />
            <div className="barcode">
              <BarcodeStripes reference={reference} />
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400 tracking-widest mt-1 sm:mt-2 font-mono ref">{reference}</p>
            </div>
          </div>

          <p className="text-center text-[10px] sm:text-xs text-gray-400 dark:text-slate-500 mt-4 sm:mt-5">
            Thank you for banking with Willowvale Credit Union
          </p>
        </div>

        {/* Actions - fixed at bottom, touch-friendly */}
        <div className="flex gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 text-sm sm:text-base font-medium hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600 transition-colors touch-manipulation"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-xl bg-primary-600 text-white text-sm sm:text-base font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors touch-manipulation"
          >
            Download / Print PDF
          </button>
        </div>
      </div>
    </div>
  );
}
